/**
 * Creation d'une enveloppe : on depose le document, puis on prepare TOUT
 * sur un seul ecran - le document en pages continues a gauche, les actions
 * a droite (signataires, ordre, champs, offre, envoi), comme le parcours du
 * signataire. Refonte demandee par le porteur le 06/10/2026 ; l'ancien
 * parcours en cinq etapes cachait le document derriere des formulaires.
 *
 * Deux exigences du CDC dominent cet ecran :
 *   - le PRIX est affiche AVANT validation ;
 *   - le credit est debite a la CREATION, pas a la signature. Un client
 *     qui decouvre le debit apres coup se sentira floue, donc on le dit
 *     avant qu'il ne valide.
 */
import { useEffect, useRef, useState } from "react";

import { api, liste, MAX_SIGNATAIRES, type Contact, type Devis, type Duplication, type Invitation, type Produit, type Reglage } from "../api";
import { LiensATransmettre } from "../components/LiensATransmettre";
import { couleurSignataire, EditeurCadres, type Cadre } from "../components/EditeurCadres";
import { Erreur, Icone, TRACES, montant, telecharger } from "../components/ui";

interface Props {
  /** Code du produit choisi depuis « Signer » (simple, avancee, qualifiee). */
  produitInitial?: string;
  /** « Dupliquer » (porteur, 12/10/2026) : tout est repris, modifiable. */
  depuis?: Duplication;
  onCree: (envelopeId: string) => void;
  onAnnuler: () => void;
}

interface SignataireSaisi {
  email: string;
  /** Porteur, 12/10/2026 : prenom et nom en deux champs ; full_name les suit. */
  prenom: string;
  nom: string;
  organisation?: string;
  /** Langue du parcours et des e-mails de CE signataire. */
  langue: string;
  full_name: string;
  auth_method: "otp_email" | "otp_sms";
  phone_e164: string;
  /** En plus du message commun (porteur, 12/10/2026). */
  message?: string;
  /** Annuaire : case « Enregistrer dans les contacts ». */
  enregistrer?: boolean;
  /** Choisi dans le carnet d'adresses : la case met le contact a jour. */
  duContact?: boolean;
  /** B-13 (12/10/2026) : approuve sans signer - aucun champ, gratuit. */
  approuve?: boolean;
  /** Lot 2bis (Franck, 15/10/2026) : ce destinataire peut-il transmettre la
   *  signature a une autre personne (la secretaire au patron) ? Absent = le
   *  reglage du compte (Parametres). */
  transmission?: Reglage;
}

/** Traitement avant signature (porteur, 12/10/2026) : remplisseur ou valideur. */
interface Personne {
  prenom: string;
  nom: string;
  email: string;
}

/** Ce qu'un remplisseur peut compléter (meme liste que le serveur). */
const KINDS_REMPLISSABLES = ["texte", "case", "radio", "liste", "date_saisie", "texte_fixe"];
/** Le nom, dans la palette de l'editeur, des champs qu'un approbateur ne remplit pas. */
const LIBELLES_NON_REMPLISSABLES: Record<string, string> = {
  signature: "Signature", paraphe: "Paraphe", nom: "Nom", email: "E-mail", date: "Date", heure: "Heure",
  date_heure: "Date et heure", mention: "Mention", piece_jointe: "Pièce jointe",
};

/** « Rémi Durand » -> prénom « Rémi », nom « Durand » (le premier mot, le reste). */
const scinder = (full_name: string | null | undefined) => {
  const [prenom = "", ...reste] = (full_name ?? "").trim().split(/\s+/);
  return { prenom, nom: reste.join(" ") };
};
const personne = (full_name: string | null | undefined, email: string): Personne => ({ ...scinder(full_name), email });

/** Etat initial repris d'une enveloppe dupliquee. */
function reprise(d: Duplication | undefined) {
  if (!d) return null;
  const signataires = d.signers.filter((s) => s.role !== "copie");
  const remplisseurs = d.intervenants.filter((i) => i.role === "remplit");
  const valideur = d.intervenants.find((i) => i.role === "valide");
  let cle = 0;
  const cadres = [
    ...signataires.flatMap((s, i) => s.champs.map((c) => ({ ...c, signataire: i }))),
    ...remplisseurs.flatMap((r, k) => r.champs.map((c) => ({ ...c, signataire: signataires.length + k }))),
  ].map((c) => ({
    ...c, cle: `dup-${cle++}`, libelle: c.libelle ?? undefined, groupe: c.groupe ?? undefined,
    options: c.options ?? undefined, obligatoire: c.obligatoire ?? true,
  }));
  return {
    signataires: signataires.map((s) => ({
      email: s.email, ...scinder(s.full_name), full_name: s.full_name ?? "", langue: s.langue ?? "fr",
      auth_method: s.auth_method, phone_e164: s.phone_e164 ?? "", message: s.message ?? "",
      approuve: s.role === "approver", transmission: s.transmission,
    })),
    cadres,
    remplisseurs: remplisseurs.map((r) => personne(r.full_name, r.email)),
    valideur: valideur ? personne(valideur.full_name, valideur.email) : null,
  };
}

/** Nom de l'offre affiche (le libelle de la base porte le partenaire). */
const NOMS_OFFRE: Record<string, string> = {
  signature_simple: "Signature eIDAS",
  signature_advanced: "Signature eIDAS avancée",
  qes_external: "Signature eIDAS qualifiée",
};

/** Le titre reprend le bouton choisi dans « Signer » (Envelopes.tsx). */
const TITRES: Record<string, string> = {
  signature_simple: "Nouvelle signature eIDAS",
  signature_advanced: "Nouvelle signature eIDAS avancée",
  qes_external: "Nouvelle signature eIDAS qualifiée",
};

const LANGUES = [
  { code: "fr", libelle: "Français" },
  { code: "en", libelle: "English" },
];


// Numero de telephone d'un signataire SMS (relecture du 25/09/2026). Le
// serveur n'accepte que le format E.164 sans espaces (schemas.SignerIn,
// meme expression que sms.E164_RE) et refuse le reste par un 422 dont le
// detail n'est pas une chaine : l'ecran n'affichait qu'une erreur generique.
// On retire donc les separateurs courants de la saisie (« +33 6 12 34 56
// 78 ») et on bloque ici, avec la raison, ce qui resterait refuse - un
// numero national (06...) n'a pas d'indicatif de pays a deviner.
const telephoneE164 = (saisie: string) => saisie.replace(/[\s.-]/g, "");
const telephoneValide = (saisie: string) => /^\+[1-9][0-9]{6,14}$/.test(telephoneE164(saisie));

export function NewEnvelope({ produitInitial, depuis, onCree, onAnnuler }: Props) {
  const [repris] = useState(() => reprise(depuis));
  const [etape, _setEtape] = useState(0);
  const [erreur, setErreur] = useState<unknown>(null);

  /** Changer d'etape efface l'erreur affichee : un message pose a l'etape 1
   *  restait visible a l'etape 5 et semblait parler de l'ecran courant
   *  (constate par le porteur le 18/09/2026). */
  const setEtape = (n: number) => {
    setErreur(null);
    _setEtape(n);
  };
  const [envoi, setEnvoi] = useState(false);
  const [modelesOuverts, setModelesOuverts] = useState(false);
  // Enveloppe deja creee dont l'envoi a echoue : on renvoie celle-ci.
  const creee = useRef<string | null>(null);

  const [titre, setTitre] = useState(depuis?.title ?? "");
  // Le dernier intitule DEDUIT d'un nom de fichier : tant que le client ne l'a
  // pas retouche, il suit le fichier ; un intitule saisi a la main est garde.
  const [titreAuto, setTitreAuto] = useState("");
  // B-31 (Franck, 02/11/2026) : plusieurs documents sont reunis en un seul
  // PDF a signer ; les cadres se posent sur ses pages, et `parties` dit a
  // quel document appartient chaque page.
  const [pieces, setPieces] = useState<{ version_id: string; filename: string }[]>(
    (depuis?.documents ?? []).map((d) => ({ version_id: d.document_version_id, filename: d.filename })));
  const [parties, setParties] = useState<{ filename: string; premiere_page: number; nb_pages: number }[]>([]);
  const nomDepot = pieces.length > 1 ? `${pieces.length} documents réunis` : pieces[0]?.filename;
  const [survol, setSurvol] = useState(false);
  const [versionId, setVersionId] = useState<string | null>(depuis?.documents[0]?.document_version_id ?? null);
  // B-24 (decision du porteur du 06/10/2026) : document deja signe. On le
  // dit, et le client choisit de continuer (ses signatures seront retirees,
  // leur image gardee) ou d'annuler. Rien ne part tant qu'il n'a pas choisi.
  // Franck, 14/10/2026 : nettoyage et conversion AU DEPOT ; le client est
  // prevenu, il n'a rien a confirmer.
  const [traitementDepot, setTraitementDepot] = useState<string[]>([]);
  // Apercu du document depose (B-08 lot 3.1) : les octets RELUS depuis le
  // serveur, pas le fichier local - c'est le document reellement stocke que
  // le client doit valider avant l'envoi. `apercu` est consomme par PDF.js,
  // `apercuBlob` sert au telechargement (PDF.js neutralise le tampon qu'on
  // lui confie, le reutiliser donnerait un fichier vide).
  const [apercu, setApercu] = useState<ArrayBuffer | null>(null);
  const [apercuBlob, setApercuBlob] = useState<Blob | null>(null);
  const [apercuKo, setApercuKo] = useState(false);
  // B-08 lot 3.2 : cadres poses par le client. Lot 2quinquies (Franck,
  // 20/10/2026, B-23) : chaque signataire a au moins un cadre de signature,
  // exige aussi par le serveur - il doit voir ou il signe.
  const [cadres, setCadres] = useState<Cadre[]>(repris?.cadres ?? []);

  const [produits, setProduits] = useState<Produit[]>([]);
  const [produitId, setProduitId] = useState<number | null>(depuis?.product_id ?? null);
  const [devis, setDevis] = useState<Devis | null>(null);

  // Lot 2quater (Franck, 19/10/2026) : langue et authentification par defaut
  // du compte (Parametres) ; la langue de l'enveloppe est celle du 1er signataire.
  const [langue, setLangue] = useState<string>("fr");
  const [authDefaut, setAuthDefaut] = useState<"otp_email" | "otp_sms">("otp_email");
  // B-07 : ordre impose par defaut ; le defaut du client est lu au montage.
  const [ordreImpose, setOrdreImpose] = useState(depuis?.signing_order_imposed ?? true);
  // Copies du document signe (16/09/2026) : adresses separees par des virgules ou des retours a la ligne.
  // Porteur, 12/10/2026 : role par destinataire. « Recoit une copie » : une
  // liste A PART des signataires - leurs numeros, et donc leurs champs, ne
  // bougent pas ; le serveur les recoit en copy_emails (copie du document signe).
  const [enCopie, setEnCopie] = useState<{ prenom: string; nom: string; email: string }[]>(
    (depuis?.copy_emails ?? []).map((email) => ({ prenom: "", nom: "", email })));
  // Traitement avant signature (porteur, 12/10/2026) : ce qui se passe AVANT
  // la V0 - des remplisseurs, puis un valideur. En option.
  const [traitement, setTraitement] = useState(Boolean(depuis?.intervenants.length));
  const [remplisseurs, setRemplisseurs] = useState<Personne[]>(repris?.remplisseurs ?? []);
  const [remplissageOrdre, setRemplissageOrdre] = useState(depuis?.remplissage_ordre_impose ?? true);
  const [valideur, setValideur] = useState<Personne | null>(repris?.valideur ?? null);
  // Liens dont l'e-mail n'a pas pu partir : a transmettre a la main.
  const [liensManuels, setLiensManuels] = useState<{ envelopeId: string; liens: Invitation[] } | null>(null);
  // Porteur, 12/10/2026 : un message par defaut du compte (Parametres),
  // propose a chaque destinataire et modifiable.
  const [messageDefaut, setMessageDefaut] = useState("");
  const [transmissionDefaut, setTransmissionDefaut] = useState<Reglage>("interdire");
  const nouveauSignataire = (message = messageDefaut): SignataireSaisi => ({
    email: "", prenom: "", nom: "", full_name: "", langue, auth_method: authDefaut, phone_e164: "", message,
  });
  const [signataires, setSignataires] = useState<SignataireSaisi[]>(repris?.signataires ?? [nouveauSignataire("")]);
  // Le signataire a qui vont les champs poses sur le document.
  const [actif, setActif] = useState(0);
  // Signataires replies a la demande (chevron), meme actifs (14/10/2026).
  const [replie, setReplie] = useState<Set<number>>(new Set());

  useEffect(() => {
    void (async () => {
      try {
        // Ni « Sécuriser » (seal…) ni « Prouver » (email_proof) : ce ne sont pas des offres de signature.
        const p = liste<Produit>(await api.produits(), "products")
          .filter((x) => !x.code.startsWith("seal") && !x.code.startsWith("email_proof"));
        setProduits(p);
        // Une offre choisie d'emblee : le cout s'affiche des l'arrivee sur
        // l'ecran de preparation, et le client peut la changer.
        // Porteur, 12/10/2026 : sans offre precise (tableau de bord), la
        // signature simple - pas « la premiere renvoyee », qui pouvait etre
        // la qualifiee.
        setProduitId((id) => id ?? p.find((x) => x.code === (produitInitial ?? "signature_simple"))?.id
          ?? p[0]?.id ?? null);
      } catch (e) {
        setErreur(e);
      }
    })();
    void (async () => {
      try {
        const p = await api.parametresClient();
        if (!depuis) {
          setOrdreImpose(p.signing_order_imposed);
          setRemplissageOrdre(p.remplissage_ordre_impose);
        }
        setLangue(p.langue_defaut);
        setAuthDefaut(p.auth_method_defaut);
        setTransmissionDefaut(p.transmission_defaut ?? "interdire");
        const m = p.message_defaut ?? "";
        setMessageDefaut(m);
        // Les signataires deja affiches (sans message saisi) le recoivent.
        setSignataires((v) => v.map((s) => ({
          ...s, ...(s.message ? {} : { message: m }),
          // Un signataire encore vierge prend les valeurs par defaut du compte.
          ...(s.email || s.prenom || s.nom ? {} : { langue: p.langue_defaut, auth_method: p.auth_method_defaut }),
        })));
      } catch {
        // Defaut inchange (impose) : ne bloque pas la creation.
      }
    })();
  }, []);

  // Duplication : l'apercu du document COPIE (le serveur l'a deja depose).
  useEffect(() => {
    const doc = depuis?.documents[0];
    if (!doc) return;
    void api.contenuDocument(doc.document_id)
      .then(async (blob) => { setApercuBlob(blob); setApercu(await blob.arrayBuffer()); }, () => setApercuKo(true));
  }, [depuis]);

  // Le prix est resolu des que l'offre change : il doit etre a l'ecran
  // avant le recapitulatif, pas decouvert a la validation.
  // B-08 lot 4 (05/10/2026) : devis du SERVEUR, meme calcul que le debit ;
  // il suit le nombre de signataires (supplement au-dela des compris).
  // B-13 : l'approbateur est gratuit, il ne compte pas dans le prix.
  const nbSignataires = signataires.filter((s) => !s.approuve).length;
  useEffect(() => {
    if (produitId === null) return;
    let annule = false;
    void (async () => {
      const d = await api.devis(produitId, Math.min(Math.max(nbSignataires, 1), MAX_SIGNATAIRES));
      // Prix indisponible : on ne bloque pas la creation, mais on le DIT
      // plutot que d'afficher un montant faux.
      if (!annule) setDevis(d);
    })();
    return () => {
      annule = true;
    };
  }, [produitId, nbSignataires]);

  // `ajouter` : les fichiers s'ajoutent a la fin des documents deja deposes
  // (leurs pages, et donc leurs cadres, ne bougent pas) ; sinon ils les
  // remplacent.
  const deposer = async (fichiers: File[], ajouter = false) => {
    const premier = fichiers[0];
    if (!premier) return;
    setErreur(null);
    const titreSaisi = !titre || titre === titreAuto ? premier.name.replace(/\.pdf$/i, "") : titre;
    if (titreSaisi !== titre && !ajouter) {
      setTitre(titreSaisi);
      setTitreAuto(titreSaisi);
    }
    setEnvoi(true);
    try {
      const deposes: { version_id: string; filename: string }[] = [];
      const fait: string[] = [];
      let documentAffiche = "";
      for (const f of fichiers) {
        const { document_id } = await api.creerDocument(titreSaisi);
        const version = await api.televerserVersion(document_id, f);
        deposes.push({ version_id: version.id, filename: f.name });
        documentAffiche = document_id;
        // Plusieurs fichiers : chaque remarque dit le sien.
        const dire = (t: string) => fait.push(fichiers.length > 1 || ajouter ? `${f.name} : ${t}` : t);
        if (version.converti === "image") dire("Votre image a été convertie en PDF d'une page.");
        const n = version.signatures_numeriques_retirees ?? 0;
        if (n > 0) {
          dire(`${n > 1 ? `${n} signatures numériques retirées` : "1 signature numérique retirée"} : `
            + "l'image de la signature reste visible sur la page, mais elle n'a plus de valeur électronique.");
        }
        if ((version.contenu_actif_retire ?? []).length > 0) {
          dire("Contenu actif retiré (JavaScript ou action automatique) : il pouvait s'exécuter à l'ouverture du document.");
        }
      }
      const liste = ajouter ? [...pieces, ...deposes] : deposes;
      if (liste.length > 1) {
        const fusion = await api.fusionnerDocuments(titreSaisi, liste.map((p) => p.version_id));
        setVersionId(fusion.version.id);
        setParties(fusion.parties);
        documentAffiche = fusion.document_id;
      } else {
        setVersionId(deposes[0]?.version_id ?? null);
        setParties([]);
      }
      setPieces(liste);
      setTraitementDepot(ajouter ? [...traitementDepot, ...fait] : fait);
      setApercu(null);
      setApercuBlob(null);
      setApercuKo(false);
      // L'apercu est un CONFORT : s'il echoue, le depot reste valable et
      // l'enveloppe peut partir. On le signale sans transformer cela en
      // erreur bloquante.
      try {
        const blob = await api.contenuDocument(documentAffiche);
        setApercuBlob(blob);
        setApercu(await blob.arrayBuffer());
      } catch {
        setApercuKo(true);
      }
    } catch (e) {
      // Le serveur valide sur les OCTETS REELS, pas sur l'extension : un
      // .pdf qui n'en est pas un est refuse ici, et son message le dit.
      // Un ajout refuse laisse les documents deja deposes en place.
      setErreur(e);
      if (!ajouter) {
        setPieces([]);
        setParties([]);
        setVersionId(null);
        setApercu(null);
        setApercuBlob(null);
      }
    } finally {
      setEnvoi(false);
    }
  };

  const majSignataire = (i: number, champ: keyof SignataireSaisi, valeur: string | boolean) =>
    setSignataires((s) => s.map((x, j) => {
      if (j !== i) return x;
      const suivant = { ...x, [champ]: valeur };
      return { ...suivant, full_name: `${suivant.prenom} ${suivant.nom}`.trim() };
    }));
  const copierMessageATous = (i: number) =>
    setSignataires((v) => v.map((x) => ({ ...x, message: v[i]?.message ?? "" })));

  // Annuaire (porteur, 12/10/2026) : a la frappe du nom ou de l'adresse, les
  // contacts enregistres ; un clic remplit le signataire.
  // Porteur, 13/10/2026 : copies, remplisseurs et valideur aussi.
  const [suggestions, setSuggestions] = useState<{ cle: string; contacts: Contact[] } | null>(null);
  const attenteRecherche = useRef<number | undefined>(undefined);
  const chercherContacts = (cle: string, q: string) => {
    window.clearTimeout(attenteRecherche.current);
    if (q.trim().length < 2) {
      setSuggestions(null);
      return;
    }
    attenteRecherche.current = window.setTimeout(() => {
      void api.contacts(q.trim()).then(({ contacts }) => setSuggestions({ cle, contacts }), () => setSuggestions(null));
    }, 250);
  };
  // Contact ancien sans prenom/nom separes : le nom complet est decoupe
  // (premier mot = prenom). Avant le 20/10/2026 il allait entier dans « nom »,
  // et le prenom saisi ensuite donnait « franck franck ben soussan ».
  const nomsDuContact = (c: Contact) => ({
    ...(c.given_name || c.family_name ? { prenom: c.given_name ?? "", nom: c.family_name ?? "" } : scinder(c.full_name)),
    email: c.email,
  });
  const choisirContact = (i: number, c: Contact) => {
    setSignataires((v) => v.map((x, j) => {
      if (j !== i) return x;
      const { prenom, nom } = nomsDuContact(c);
      return {
        ...x, email: c.email, prenom, nom, full_name: `${prenom} ${nom}`.trim(), organisation: c.organisation ?? "",
        auth_method: c.auth_method, phone_e164: c.phone_e164 ?? "", duContact: true,
      };
    }));
    setSuggestions(null);
  };
  const cacherSuggestions = () => window.setTimeout(() => setSuggestions(null), 200);
  const listeContacts = (cle: string, choisir: (c: Contact) => void) =>
    suggestions?.cle === cle && suggestions.contacts.length > 0 && (
      <ul className="suggestions" role="listbox" aria-label="Contacts">
        {suggestions.contacts.map((c) => (
          <li key={c.id}>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); choisir(c); setSuggestions(null); }}>
              <strong>{c.full_name || c.email}</strong>
              {c.full_name && <span className="muted"> · {c.email}</span>}
              {c.organisation && <span className="muted"> · {c.organisation}</span>}
            </button>
          </li>
        ))}
      </ul>
    );

  /** T-33 : l'ordre de la liste est l'ordre PROPOSE aux signataires. Les
   *  champs poses suivent leur signataire. */
  const deplacer = (i: number, pas: -1 | 1) => {
    const j = i + pas;
    if (j < 0 || j >= signataires.length) return;
    setSignataires((v) => {
      const copie = [...v];
      const a = copie[i];
      const b = copie[j];
      if (!a || !b) return v;
      copie[i] = b;
      copie[j] = a;
      return copie;
    });
    setCadres((cs) => cs.map((c) => (c.signataire === i ? { ...c, signataire: j }
      : c.signataire === j ? { ...c, signataire: i } : c)));
    setActif((a) => (a === i ? j : a === j ? i : a));
  };

  const versCopie = (i: number) => {
    const s = signataires[i];
    if (!s || signataires.length < 2) return;
    setEnCopie((v) => [...v, { prenom: s.prenom, nom: s.nom, email: s.email }]);
    retirer(i);
  };
  const versSignataire = (k: number) => {
    const c = enCopie[k];
    if (!c) return;
    setEnCopie((v) => v.filter((_, j) => j !== k));
    decalerRemplisseurs();
    setSignataires((v) => [...v, { ...nouveauSignataire(), prenom: c.prenom, nom: c.nom, email: c.email,
                                   full_name: `${c.prenom} ${c.nom}`.trim() }]);
  };
  const majCopie = (k: number, champ: "prenom" | "nom" | "email", valeur: string) =>
    setEnCopie((v) => v.map((x, j) => (j === k ? { ...x, [champ]: valeur } : x)));

  /** Les champs d'un remplisseur sont au rang signataires + k : un
   *  signataire ajoute en fin de liste les decale d'un cran. */
  const decalerRemplisseurs = () =>
    setCadres((cs) => cs.map((c) => (c.signataire >= signataires.length ? { ...c, signataire: c.signataire + 1 } : c)));
  const retirerRemplisseur = (k: number) => {
    const rang = signataires.length + k;
    setRemplisseurs((v) => v.filter((_, j) => j !== k));
    setCadres((cs) => cs.filter((c) => c.signataire !== rang)
      .map((c) => (c.signataire > rang ? { ...c, signataire: c.signataire - 1 } : c)));
    setActif(0);
  };
  const majPersonne = (p: Personne, champ: keyof Personne, valeur: string): Personne => ({ ...p, [champ]: valeur });

  const retirer = (i: number) => {
    setSignataires((v) => v.filter((_, j) => j !== i));
    setCadres((cs) => cs.filter((c) => c.signataire !== i)
      .map((c) => (c.signataire > i ? { ...c, signataire: c.signataire - 1 } : c)));
    setActif((a) => (a > i ? a - 1 : a === i ? 0 : a));
  };

  const copies = enCopie.map((c) => c.email.trim()).filter((a) => a !== "");
  const copiesValides = copies.length <= 20 && copies.every((a) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a));

  // Porteur, 12/10/2026 : pas deux signataires avec la meme adresse.
  const adresses = signataires.map((s) => s.email.trim().toLowerCase()).filter((a) => a !== "");
  const copiesEnDouble = new Set(copies.map((a) => a.toLowerCase())).size !== copies.length;
  const adresseEnDouble = new Set(adresses).size !== adresses.length;
  // Lot 2quater (Franck, 19/10/2026) : prenom ET nom obligatoires, signataire comme approbateur.
  const signatairesValides = copiesValides && !adresseEnDouble && !copiesEnDouble && signataires.every(
    (s) => s.prenom.trim() !== "" && s.nom.trim() !== "" && s.email.includes("@")
      && (s.auth_method === "otp_email" || telephoneValide(s.phone_e164)),
  );
  const intervenants = traitement ? [...remplisseurs, ...(valideur ? [valideur] : [])] : [];
  const adressesIntervenants = intervenants.map((p) => p.email.trim().toLowerCase());
  const traitementValide = !traitement || (intervenants.length > 0
    && adressesIntervenants.every((a) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a))
    && new Set(adressesIntervenants).size === adressesIntervenants.length);

  const creer = async () => {
    if (!versionId || produitId === null) return;
    setEnvoi(true);
    setErreur(null);
    try {
      if (creee.current) {
        await envoyer(creee.current);
        return;
      }
      const { envelope_id } = await api.creerEnveloppe({
        title: titre,
        product_id: produitId,
        document_version_ids: [versionId],
        langue: signataires[0]?.langue ?? langue,
        signing_order_imposed: ordreImpose,

        copy_emails: copies,
        ...(traitement ? {
          remplissage_ordre_impose: remplissageOrdre,
          intervenants: [
            ...remplisseurs.map((r, k) => ({
              email: r.email.trim(), full_name: `${r.prenom} ${r.nom}`.trim() || null, role: "remplit" as const,
              champs: cadres
                .filter((c) => c.signataire === signataires.length + k)
                .map(({ kind, page_no, x, y, w, h, libelle, obligatoire, groupe, options }) =>
                  ({ kind, page_no, x, y, w, h, libelle: libelle || null, obligatoire: obligatoire ?? true,
                     groupe: groupe || null,
                     options: kind === "liste" ? (options ?? []).map((o) => o.trim()).filter(Boolean) : null })),
            })),
            ...(valideur ? [{ email: valideur.email.trim(), full_name: `${valideur.prenom} ${valideur.nom}`.trim() || null,
                              role: "valide" as const, champs: [] }] : []),
          ],
        } : {}),
        signers: signataires.map((s, i) => ({
          email: s.email.trim(),
          full_name: s.full_name.trim() || undefined,
          given_name: s.prenom.trim() || undefined,
          family_name: s.nom.trim() || undefined,
          organisation: s.organisation?.trim() || undefined,
          langue: s.langue,
          auth_method: s.auth_method,
          role: s.approuve ? "approver" : "signer",
          message: s.message?.trim() || undefined,
          enregistrer_contact: s.enregistrer || undefined,
          transmission: s.transmission ?? transmissionDefaut,
          ...(s.auth_method === "otp_sms" ? { phone_e164: telephoneE164(s.phone_e164) } : {}),
          // Les cadres voyagent AVEC la creation : ils appartiennent a un
          // signataire dont l'identifiant naît la (cf. services/envelopes).
          champs: cadres
            .filter((c) => c.signataire === i)
            .map(({ kind, page_no, x, y, w, h, libelle, obligatoire, groupe, options }) =>
              ({ kind, page_no, x, y, w, h, libelle: libelle || null, obligatoire: obligatoire ?? true,
                 groupe: groupe || null,
                 options: kind === "liste" ? (options ?? []).map((o) => o.trim()).filter(Boolean) : null })),
        })),
      });
      creee.current = envelope_id;
      await envoyer(envelope_id);
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  };

  // Franck, 14/10/2026 : plus de page « Vérifiez le document qui va partir ».
  // « Envoyer ce document » cree l'enveloppe et l'envoie ; le serveur
  // prepare le PDF, en enregistre l'empreinte et debite. Si l'envoi echoue
  // (credit insuffisant...), un nouveau clic renvoie la MEME enveloppe.
  const envoyer = async (envelope_id: string) => {
    if (traitement) {
      // Traitement avant signature : les intervenants passent par leur
      // espace, pas de lien de signature.
      await api.envoyerEnveloppe(envelope_id);
      onCree(envelope_id);
      return;
    }
    const { signers: invites } = await api.envoyerEnveloppe(envelope_id);
    const nonParties = invites.filter((i) => !i.email_sent);
    if (nonParties.length > 0) {
      // Decision du 16/09/2026 : l'enveloppe est partie, mais ces
      // signataires n'ont rien recu - on donne le lien.
      setLiensManuels({ envelopeId: envelope_id, liens: nonParties });
    } else {
      onCree(envelope_id);
    }
  };

  if (liensManuels) {
    return (
      <>
        <h1 className="page-title">Enveloppe envoyée</h1>
        <LiensATransmettre liens={liensManuels.liens} />
        <div className="actions">
          <button type="button" className="btn btn--primary" onClick={() => onCree(liensManuels.envelopeId)}>
            Voir l'enveloppe
          </button>
        </div>
      </>
    );
  }

  const noms = [
    ...signataires.map((s, i) => {
      const nom = s.full_name.trim() || s.email.trim() || `Signataire ${i + 1}`;
      return s.approuve ? `Approbateur : ${nom}` : nom;
    }),
    // Les remplisseurs posent leurs champs comme un signataire (12/10/2026).
    ...(traitement ? remplisseurs : []).map((r, k) =>
      `Remplit : ${`${r.prenom} ${r.nom}`.trim() || r.email.trim() || `remplisseur ${k + 1}`}`),
  ];
  const champsNonRemplissables = cadres.some((c) => c.signataire >= signataires.length
    && !KINDS_REMPLISSABLES.includes(c.kind));
  const mentionsVides = cadres.some((c) => (c.kind === "mention" || c.kind === "texte_fixe" || c.kind === "radio"
    || c.kind === "piece_jointe")
    && !c.libelle?.trim());
  // Porteur, 12/10/2026 : « un bouton radio c'est un choix entre plusieurs
  // options, sinon cela devient une case à cocher ».
  // Porteur, 12/10/2026 : l'approbateur « ne signe pas mais pourra remplir
  // des champs » - les memes que ceux d'un remplisseur, jamais une signature.
  const cadreApprobateur = cadres.find((c) => signataires[c.signataire]?.approuve
    && !KINDS_REMPLISSABLES.includes(c.kind));
  const champsApprobateur = cadreApprobateur !== undefined;
  const listeIncomplete = cadres.some((c) => c.kind === "liste"
    && new Set((c.options ?? []).map((o) => o.trim()).filter(Boolean)).size < 2);
  const radioSeul = cadres.some((c) => c.kind === "radio"
    && cadres.filter((x) => x.kind === "radio" && x.signataire === c.signataire && x.groupe === c.groupe).length < 2);
  // Lot 2quinquies (B-23) : le premier signataire (pas approbateur) sans cadre de signature.
  const iSansSignature = signataires.findIndex((s, i) => !s.approuve
    && !cadres.some((c) => c.signataire === i && c.kind === "signature"));
  const sansSignature = iSansSignature < 0 ? null : signataires[iSansSignature]?.full_name.trim()
    || signataires[iSansSignature]?.email.trim() || `le signataire ${iSansSignature + 1}`;
  const pret = Boolean(versionId) && titre.trim() !== "" && produitId !== null && signatairesValides && !mentionsVides
    && traitementValide && !champsNonRemplissables
    && !radioSeul && !listeIncomplete && !champsApprobateur && nbSignataires > 0 && sansSignature === null;
  const raison = !titre.trim() ? "Donnez un intitulé au document."
    : !signatairesValides ? "Complétez le prénom, le nom et l'adresse e-mail (et le téléphone pour un code par SMS) de chaque signataire."
      : mentionsVides ? "Une mention, un texte fixe ou une option de bouton radio n'a pas de texte."
        : radioSeul ? "Un groupe de boutons radio n'a qu'une option : ajoutez-en une, ou utilisez une case à cocher."
          : cadreApprobateur ? `Le champ « ${LIBELLES_NON_REMPLISSABLES[cadreApprobateur.kind] ?? cadreApprobateur.kind} » de la page ${cadreApprobateur.page_no} est attribué à ${noms[cadreApprobateur.signataire]?.replace(/^Approbateur : /, "")}, qui est approbateur. Un approbateur ne signe pas : il complète du texte, une date à saisir, une case, un choix ou une liste. Attribuez ce champ à un signataire ou supprimez-le.`
          : !traitementValide ? "Traitement avant signature : ajoutez au moins une personne, chacune avec sa propre adresse e-mail."
          : champsNonRemplissables ? "Un remplisseur complète du texte, une date, une case, un choix ou une liste : pas de signature ni de paraphe."
          : nbSignataires === 0 ? "Il faut au moins une personne qui signe."
          : listeIncomplete ? "Une liste de choix propose moins de deux choix."
          : sansSignature ? `Posez un cadre « Signature » pour ${sansSignature} : chaque signataire doit voir où il signe.`
        : produitId === null ? "Choisissez une offre." : null;
  const raisonEtape0 = !versionId ? "Déposez le document à signer."
    : !titre.trim() ? "Donnez un intitulé au document."
        : !signatairesValides ? "Complétez le prénom, le nom et l'adresse e-mail (et le téléphone pour un code par SMS) de chaque signataire."
          : !traitementValide ? "Traitement avant signature : donnez une adresse e-mail à chaque personne qui remplit ou valide (une adresse différente pour chacune), ou retirez la ligne vide."
            : produitId === null ? "Choisissez une offre." : null;
  const telechargerApercu = () => {
    if (apercuBlob) telecharger(apercuBlob, pieces.length > 1 ? `${titre || "documents"}.pdf` : nomDepot || "document.pdf");
  };

  /* ------------------------------------------------ 1. Depot du document */
  if (etape === 0) {
    return (
      <>
        {/* Porteur, 12/10/2026 : le titre reprend le bouton choisi dans
            « Signer » - l'offre est donc deja choisie, plus de liste. */}
        <h1 className="page-title">{TITRES[produitInitial ?? ""] ?? "Nouvelle enveloppe"}</h1>
        <div className="card stack">
          <Erreur erreur={erreur} />
          {/* Franck, 01/11/2026 : « condenser au maximum la premiere partie » -
              une fois le document depose, intitule et fichier sur une ligne. */}
          <div className={versionId ? "entete-doc" : "stack"}>
          <div className="field">
            <label className={versionId ? "sr-only" : "field-label"} htmlFor="titre">
              Intitulé
            </label>
            <input
              id="titre"
              className="input"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Contrat de prestation 2026"
            />
          </div>

          <div
            className={`dropzone ${survol ? "dropzone--over" : ""} ${versionId ? "dropzone--compacte" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setSurvol(true);
            }}
            onDragLeave={() => setSurvol(false)}
            onDrop={(e) => {
              e.preventDefault();
              setSurvol(false);
              void deposer(Array.from(e.dataTransfer.files ?? []));
            }}
            onClick={() => document.getElementById("fichier")?.click()}
          >
            <input
              id="fichier"
              type="file"
              multiple
              accept="application/pdf,.pdf,image/png,image/jpeg,.png,.jpg,.jpeg"
              style={{ display: "none" }}
              onChange={(e) => {
                void deposer(Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
            />
            {envoi ? (
              <div className="spinner" role="status" aria-label="Envoi en cours" />
            ) : versionId ? (
              <strong title="Cliquez pour choisir d'autres documents">{nomDepot}</strong>
            ) : (
              <>
                <strong>Glissez votre PDF ici</strong>
                <p className="muted" style={{ margin: "0.3rem 0 0" }}>
                  ou cliquez pour le choisir. Plusieurs fichiers : ils sont réunis en un seul document à signer.
                  PDF, PNG ou JPEG, 50 Mo maximum. Word et Excel : enregistrez-les d'abord en PDF.
                </p>
              </>
            )}
          </div>
          </div>

          {versionId && !envoi && (
            <div className="stack" style={{ gap: "0.3rem" }}>
              {parties.length > 1 && (
                <ul className="muted" style={{ margin: 0, paddingLeft: "1.2rem" }}>
                  {parties.map((p) => (
                    <li key={p.premiere_page}>
                      {p.filename} : {p.nb_pages > 1
                        ? `pages ${p.premiere_page} à ${p.premiere_page + p.nb_pages - 1}`
                        : `page ${p.premiere_page}`}
                    </li>
                  ))}
                </ul>
              )}
              <div>
                <button type="button" className="btn btn--link btn--inline"
                        onClick={() => document.getElementById("fichier-ajout")?.click()}>
                  Ajouter un document
                </button>
                <input
                  id="fichier-ajout"
                  type="file"
                  multiple
                  accept="application/pdf,.pdf,image/png,image/jpeg,.png,.jpg,.jpeg"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    void deposer(Array.from(e.target.files ?? []), true);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
          )}

          {versionId && traitementDepot.length > 0 && (
            <div className="alert alert--info" role="status">
              <p style={{ margin: 0 }}>
                <strong>Document préparé pour la signature :</strong> {traitementDepot.join(" ")}{" "}
                <span className="muted">Vérifiez le résultat dans l'aperçu avant l'envoi.</span>
              </p>
            </div>
          )}

        </div>

        <div className="card">
          {adresseEnDouble && <p className="muted" style={{ margin: "0 0 0.75rem" }}>Deux signataires ont la même adresse e-mail.</p>}
          {/* DESIGN.md : annuler a gauche, valider a droite (critique du 13/10/2026). */}
          {/* Porteur, 13/10/2026 : un bouton grise dit pourquoi - en info-bulle
              depuis le 01/11/2026 (Franck : la phrase prenait une ligne). */}
          <div className="pied">
            <button type="button" className="btn btn--secondary" onClick={onAnnuler}>
              Annuler
            </button>
            {/* Franck, 01/11/2026 : les trois ajouts rejoignent cette barre - une
                seule ligne si la largeur le permet, sinon deux. La barre est
                placee sous le document, au-dessus des signataires. */}
            {versionId && (
            <div className="pied__ajouts">
              <button type="button" className={traitement ? "btn btn--secondary btn--actif" : "btn btn--secondary"}
                      aria-pressed={traitement}
                      onClick={() => {
                        const active = !traitement;
                        setTraitement(active);
                        if (active && remplisseurs.length === 0 && !valideur) {
                          setRemplisseurs([{ prenom: "", nom: "", email: "" }]);
                        }
                      }}>
                {traitement ? "✓ Traitement avant signature" : "Traitement avant signature"}
              </button>
              <button
                type="button"
                className="btn btn--secondary"
                disabled={signataires.length >= MAX_SIGNATAIRES}
                title={signataires.length >= MAX_SIGNATAIRES ? `${MAX_SIGNATAIRES} signataires au plus par enveloppe` : undefined}
                onClick={() => {
                  decalerRemplisseurs();
                  setSignataires((v) => [...v, nouveauSignataire()]);
                  setActif(signataires.length);
                }}
              >
                + Ajouter un signataire
              </button>
              <button type="button" className="btn btn--secondary" disabled={enCopie.length >= 20}
                      onClick={() => setEnCopie((v) => [...v, { prenom: "", nom: "", email: "" }])}>
                Envoyer une copie des documents signés
              </button>
            </div>
            )}
            <div className="pied__suite">
              {/* Porteur, 12/10/2026 : « choisir un modèle » des cette etape. */}
              <button
                type="button"
                className="btn btn--secondary"
                disabled={raisonEtape0 !== null}
                title={raisonEtape0 ?? undefined}
                onClick={() => { setModelesOuverts(true); setEtape(1); }}
              >
                Choisir un modèle
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={raisonEtape0 !== null}
                title={raisonEtape0 ?? undefined}
                onClick={() => setEtape(1)}
              >
                Personnaliser le document à signer →
              </button>
            </div>
          </div>
        </div>

        {versionId && (
          <div className="card stack etape__suite">
        <section className="panneau">
          {/* Sequentiel / parallele : visible d'emblee (06/10/2026), sur la
              ligne du titre (14/10/2026). */}
          <div className="panneau__entete">
          <h2 className="panneau__titre">Signataires</h2>
          <div className="segment segment--compact" role="radiogroup" aria-label="Ordre de signature">
            <button type="button" role="radio" aria-checked={ordreImpose}
                    className={ordreImpose ? "segment__choix segment__choix--actif" : "segment__choix"}
                    onClick={() => setOrdreImpose(true)}>
              Séquentiel
            </button>
            <button type="button" role="radio" aria-checked={!ordreImpose}
                    className={!ordreImpose ? "segment__choix segment__choix--actif" : "segment__choix"}
                    onClick={() => setOrdreImpose(false)}>
              Parallèle
            </button>
          </div>
          </div>
          <p className="muted panneau__aide">
            {ordreImpose
              ? "Chacun reçoit son lien quand le précédent a signé, dans l'ordre de la liste."
              : "Tous reçoivent leur lien en même temps et signent dans l'ordre qu'ils veulent."}
          </p>

          {signataires.map((s, i) => {
            // Porteur, 14/10/2026 : un signataire complet se replie sur sa
            // premiere ligne ; un clic le deplie, le chevron le replie.
            const complet = s.prenom.trim() !== "" && s.nom.trim() !== "" && s.email.includes("@")
              && (s.auth_method === "otp_email" || telephoneValide(s.phone_e164));
            const deplie = !complet || (i === actif && !replie.has(i));
            return (
            <div
              key={i}
              className={i === actif ? "signataire signataire--actif" : "signataire"}
              onFocus={() => setActif(i)}
              onClick={() => { setActif(i); if (!deplie) setReplie((r) => { const n = new Set(r); n.delete(i); return n; }); }}
            >
              <div className="signataire__tete">
                <span className="signataire__rang" style={{ background: couleurSignataire(i) }}>
                  {ordreImpose ? i + 1 : "•"}
                </span>
                <input className="input" placeholder="Prénom" value={s.prenom}
                       aria-label={`Prénom du signataire ${i + 1}`}
                       onChange={(e) => { majSignataire(i, "prenom", e.target.value); chercherContacts(`s${i}`, e.target.value); }}
                       onBlur={() => window.setTimeout(() => setSuggestions(null), 200)} />
                <input className="input" placeholder="Nom" value={s.nom}
                       aria-label={`Nom du signataire ${i + 1}`}
                       onChange={(e) => { majSignataire(i, "nom", e.target.value); chercherContacts(`s${i}`, e.target.value); }}
                       onBlur={() => window.setTimeout(() => setSuggestions(null), 200)} />
                <select className="select signataire__role" value={s.approuve ? "approbateur" : "signer"}
                        aria-label={`Rôle du destinataire ${i + 1}`}
                        onChange={(e) => {
                          if (e.target.value === "copie") versCopie(i);
                          else majSignataire(i, "approuve", e.target.value === "approbateur");
                        }}>
                  <option value="signer">Doit signer</option>
                  <option value="approbateur">Approuve (sans signer)</option>
                  <option value="copie" disabled={signataires.length < 2}>Reçoit une copie</option>
                </select>
                <span className="signataire__actions">
                  {complet && (
                    <button type="button" aria-label={deplie ? "Replier ce signataire" : "Déplier ce signataire"}
                            aria-expanded={deplie}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActif(i);
                              setReplie((r) => { const n = new Set(r); if (deplie) n.add(i); else n.delete(i); return n; });
                            }}>
                      <Icone d={deplie ? TRACES.chevronHaut : TRACES.chevronBas} taille={16} />
                    </button>
                  )}
                {signataires.length > 1 && (
                  <>
                    <button type="button" aria-label="Monter" disabled={i === 0}
                            onClick={(e) => { e.stopPropagation(); deplacer(i, -1); }}><Icone d={TRACES.haut} taille={16} /></button>
                    <button type="button" aria-label="Descendre" disabled={i === signataires.length - 1}
                            onClick={(e) => { e.stopPropagation(); deplacer(i, 1); }}><Icone d={TRACES.bas} taille={16} /></button>
                    <button type="button" aria-label="Retirer ce signataire"
                            onClick={(e) => { e.stopPropagation(); retirer(i); }}><Icone d={TRACES.croix} taille={16} /></button>
                  </>
                )}
                </span>
              </div>
              {!deplie ? (
                <p className="muted signataire__resume">
                  {s.email}{s.organisation ? ` · ${s.organisation}` : ""} · {s.auth_method === "otp_sms" ? "code par SMS" : "code par e-mail"}
                </p>
              ) : (<>
              {/* Porteur, 14/10/2026 : tous ces champs sur deux lignes. */}
              <div className="ligne ligne--trois">
              <input className="input" type="email" placeholder="Adresse e-mail" value={s.email}
                     aria-label={`E-mail du signataire ${i + 1}`}
                     onChange={(e) => { majSignataire(i, "email", e.target.value); chercherContacts(`s${i}`, e.target.value); }}
                     onBlur={() => window.setTimeout(() => setSuggestions(null), 200)} />
                <input className="input" placeholder="Organisation (facultatif)" value={s.organisation ?? ""}
                       aria-label={`Organisation du signataire ${i + 1}`}
                       onChange={(e) => majSignataire(i, "organisation", e.target.value)} />
                <input className="input" value={s.phone_e164}
                       placeholder={s.auth_method === "otp_sms" ? "Téléphone (+33612345678)" : "Téléphone (facultatif)"}
                       aria-label="Téléphone"
                       aria-invalid={s.phone_e164.trim() !== "" && !telephoneValide(s.phone_e164) ? true : undefined}
                       onChange={(e) => majSignataire(i, "phone_e164", e.target.value)} />
              </div>
              {listeContacts(`s${i}`, (c) => choisirContact(i, c))}
              <div className="ligne ligne--trois">
                {/* Canal fixe une fois pour toutes : un signataire en e-mail ne
                    basculera jamais en SMS payant en cours de parcours. */}
                <select className="select" value={s.auth_method} aria-label="Envoi du code"
                        onChange={(e) => majSignataire(i, "auth_method", e.target.value)}>
                  <option value="otp_email">Code par e-mail</option>
                  <option value="otp_sms">Code par SMS (facturé)</option>
                </select>
                {/* Porteur, 12/10/2026 : la langue est celle du signataire. */}
                <select className="select" value={s.langue} aria-label={`Langue du signataire ${i + 1}`}
                        onChange={(e) => majSignataire(i, "langue", e.target.value)}>
                  {LANGUES.map((l) => <option key={l.code} value={l.code}>{l.libelle}</option>)}
                </select>
              <label className="case-ligne">
                <input type="checkbox" checked={Boolean(s.enregistrer)}
                       onChange={(e) => majSignataire(i, "enregistrer", e.target.checked)} />
                {s.duContact ? "Mettre à jour ce contact" : "Enregistrer dans les contacts"}
              </label>
              {/* Lot 2bis : prerempli avec le reglage du compte (Parametres). */}
              <select className="select" value={s.transmission ?? transmissionDefaut}
                      aria-label={`Transmission de la signature par le signataire ${i + 1}`}
                      onChange={(e) => majSignataire(i, "transmission", e.target.value)}>
                <option value="interdire">Transmission à une autre personne : interdite</option>
                <option value="demander">Transmission : avec votre accord</option>
                <option value="autoriser">Transmission : autorisée</option>
              </select>
              </div>
              {/* Porteur, 12/10/2026 : le message est TOUJOURS presente, prerempli
                  par le message par defaut du compte, modifiable, et copiable a
                  tous les destinataires. */}
              <div className="message__entete">
                <label className="field-label" htmlFor={`message-${i}`}>Message</label>
                {signataires.length > 1 && (
                  <button type="button" className="btn btn--link btn--inline" onClick={() => copierMessageATous(i)}>
                    Copier ce message à tous les destinataires
                  </button>
                )}
              </div>
              <textarea id={`message-${i}`} className="input" rows={5} maxLength={2000} value={s.message ?? ""}
                        aria-label={`Message pour le signataire ${i + 1}`}
                        onChange={(e) => majSignataire(i, "message", e.target.value)} />
              </>)}
            </div>
            );
          })}
          {enCopie.map((c, k) => (
            <div key={`copie-${k}`} className="signataire signataire--copie">
              <div className="signataire__tete">
                <span className="signataire__rang" aria-hidden="true"><Icone d={TRACES.enveloppe} taille={14} /></span>
                <input className="input" placeholder="Prénom" value={c.prenom} aria-label={`Prénom de la copie ${k + 1}`}
                       onChange={(e) => { majCopie(k, "prenom", e.target.value); chercherContacts(`c${k}`, e.target.value); }}
                       onBlur={cacherSuggestions} />
                <input className="input" placeholder="Nom" value={c.nom} aria-label={`Nom de la copie ${k + 1}`}
                       onChange={(e) => { majCopie(k, "nom", e.target.value); chercherContacts(`c${k}`, e.target.value); }}
                       onBlur={cacherSuggestions} />
                <select className="select signataire__role" value="copie" aria-label={`Rôle de la copie ${k + 1}`}
                        onChange={(e) => { if (e.target.value === "signer") versSignataire(k); }}>
                  <option value="signer">Doit signer</option>
                  <option value="copie">Reçoit une copie</option>
                </select>
                <span className="signataire__actions">
                  <button type="button" aria-label="Retirer cette copie"
                          onClick={() => setEnCopie((v) => v.filter((_, j) => j !== k))}><Icone d={TRACES.croix} taille={16} /></button>
                </span>
              </div>
              <input className="input" type="email" placeholder="Adresse e-mail" value={c.email}
                     aria-label={`E-mail de la copie ${k + 1}`} onBlur={cacherSuggestions}
                     onChange={(e) => { majCopie(k, "email", e.target.value); chercherContacts(`c${k}`, e.target.value); }} />
              {listeContacts(`c${k}`, (c) => setEnCopie((v) => v.map((x, j) => (j === k ? nomsDuContact(c) : x))))}
              <p className="muted panneau__aide" style={{ margin: 0 }}>Reçoit le document signé quand tous ont signé.</p>
            </div>
          ))}
          {(copiesEnDouble || !copiesValides) && (
            <p className="muted panneau__aide" style={{ color: "#b3261e" }}>
              Une adresse en copie n'est pas valide, est en double, ou il y en a plus de 20.
            </p>
          )}
        </section>

        {traitement && (
        <section className="panneau">
          <div className="panneau__entete">
            <h2 className="panneau__titre">Traitement avant signature</h2>
            <button type="button" className="btn btn--link btn--inline" onClick={() => setTraitement(false)}>
              Retirer
            </button>
          </div>
          <p className="muted panneau__aide">
            Rien ne part en signature avant que ces personnes aient terminé. Chacune agit depuis son espace
            Sign365 : sans compte, elle reçoit un e-mail pour en créer un.
          </p>
            <>
              <h3 className="panneau__sous-titre">Remplit</h3>
              {remplisseurs.length > 1 && (
                <div className="segment" role="radiogroup" aria-label="Ordre des remplisseurs">
                  <button type="button" role="radio" aria-checked={remplissageOrdre}
                          className={remplissageOrdre ? "segment__choix segment__choix--actif" : "segment__choix"}
                          onClick={() => setRemplissageOrdre(true)}>Séquentiel</button>
                  <button type="button" role="radio" aria-checked={!remplissageOrdre}
                          className={!remplissageOrdre ? "segment__choix segment__choix--actif" : "segment__choix"}
                          onClick={() => setRemplissageOrdre(false)}>Parallèle</button>
                </div>
              )}
              {remplisseurs.map((r, k) => (
                <div key={`remplit-${k}`} className="signataire">
                  <div className="signataire__tete">
                    <span className="signataire__rang" style={{ background: couleurSignataire(signataires.length + k) }}>
                      {remplissageOrdre ? k + 1 : "•"}
                    </span>
                    <input className="input" placeholder="Prénom" value={r.prenom} aria-label={`Prénom du remplisseur ${k + 1}`}
                           onBlur={cacherSuggestions}
                           onChange={(e) => { setRemplisseurs((v) => v.map((x, j) => (j === k ? majPersonne(x, "prenom", e.target.value) : x))); chercherContacts(`r${k}`, e.target.value); }} />
                    <input className="input" placeholder="Nom" value={r.nom} aria-label={`Nom du remplisseur ${k + 1}`}
                           onBlur={cacherSuggestions}
                           onChange={(e) => { setRemplisseurs((v) => v.map((x, j) => (j === k ? majPersonne(x, "nom", e.target.value) : x))); chercherContacts(`r${k}`, e.target.value); }} />
                    <span className="signataire__actions">
                      <button type="button" aria-label="Retirer ce remplisseur" onClick={() => retirerRemplisseur(k)}><Icone d={TRACES.croix} taille={16} /></button>
                    </span>
                  </div>
                  <input className="input" type="email" placeholder="Adresse e-mail" value={r.email}
                         aria-label={`E-mail du remplisseur ${k + 1}`}
                         onBlur={cacherSuggestions}
                         onChange={(e) => { setRemplisseurs((v) => v.map((x, j) => (j === k ? majPersonne(x, "email", e.target.value) : x))); chercherContacts(`r${k}`, e.target.value); }} />
                  {listeContacts(`r${k}`, (c) => setRemplisseurs((v) => v.map((x, j) => (j === k ? nomsDuContact(c) : x))))}
                </div>
              ))}
              <button type="button" className="btn btn--link btn--inline" disabled={remplisseurs.length >= 19}
                      onClick={() => setRemplisseurs((v) => [...v, { prenom: "", nom: "", email: "" }])}>
                + Ajouter une personne qui remplit
              </button>
              <p className="muted panneau__aide" style={{ margin: 0 }}>
                Ses champs se posent à l'étape suivante, comme ceux d'un signataire.
              </p>

              <h3 className="panneau__sous-titre">Valide avant envoi</h3>
              {valideur ? (
                <div className="signataire">
                  <div className="signataire__tete">
                    <span className="signataire__rang" aria-hidden="true" style={{ background: "var(--navy)" }}><Icone d={TRACES.coche} taille={14} /></span>
                    <input className="input" placeholder="Prénom" value={valideur.prenom} aria-label="Prénom du valideur"
                           onBlur={cacherSuggestions}
                           onChange={(e) => { setValideur(majPersonne(valideur, "prenom", e.target.value)); chercherContacts("v", e.target.value); }} />
                    <input className="input" placeholder="Nom" value={valideur.nom} aria-label="Nom du valideur"
                           onBlur={cacherSuggestions}
                           onChange={(e) => { setValideur(majPersonne(valideur, "nom", e.target.value)); chercherContacts("v", e.target.value); }} />
                    <span className="signataire__actions">
                      <button type="button" aria-label="Retirer le valideur" onClick={() => setValideur(null)}><Icone d={TRACES.croix} taille={16} /></button>
                    </span>
                  </div>
                  <input className="input" type="email" placeholder="Adresse e-mail" value={valideur.email}
                         aria-label="E-mail du valideur"
                         onBlur={cacherSuggestions}
                         onChange={(e) => { setValideur(majPersonne(valideur, "email", e.target.value)); chercherContacts("v", e.target.value); }} />
                  {listeContacts("v", (c) => setValideur(nomsDuContact(c)))}
                  <p className="muted panneau__aide" style={{ margin: 0 }}>
                    Passe après les remplisseurs. S'il refuse, rien ne part en signature et l'enveloppe est facturée au tarif « enveloppe non signée ».
                  </p>
                </div>
              ) : (
                <button type="button" className="btn btn--link btn--inline"
                        onClick={() => setValideur({ prenom: "", nom: "", email: "" })}>
                  + Ajouter une personne qui valide avant envoi
                </button>
              )}
            </>
        </section>
        )}
          </div>
        )}

      </>
    );
  }

  /* -------------------------------------------- 2. Preparation, un ecran */
  const avant = (
    <>
      <section className="panneau">
        <input className="input panneau__intitule" value={titre} aria-label="Intitulé"
               onChange={(e) => setTitre(e.target.value)} />

      </section>

    </>
  );

  const apres = (
    <>

      {/* Recapitulatif (Franck, 14/10/2026 : ici, plus sur une page a part). */}
      <section className="panneau">
        <h2 className="panneau__titre">Récapitulatif</h2>
        <ol className="recap">
          {signataires.map((s, i) => (
            <li key={i}>
              <span className="recap__nom">{s.full_name.trim() || s.email}{s.approuve ? " (approuve sans signer)" : ""}</span>
              <span className="muted">{s.email} · {s.auth_method === "otp_sms" ? "code par SMS" : "code par e-mail"}</span>
            </li>
          ))}
        </ol>
        {signataires.length > 1 && (
          <p className="muted panneau__aide">
            {ordreImpose ? "Ordre imposé : chacun reçoit son lien quand le précédent a signé." : "Ordre libre : tous reçoivent leur lien en même temps."}
          </p>
        )}
        {copies.length > 0 && <p className="muted panneau__aide">Copie à : {copies.join(", ")}</p>}
      </section>
      <section className="panneau panneau--envoi">
        <p className="envoi__cout">
          {devis !== null ? (
            <>
              <strong>{montant(devis.total_minor)}</strong>
              {devis.signataires_en_plus > 0 && ` dont ${montant(devis.supplement_minor)} pour ${devis.signataires_en_plus} signataire${devis.signataires_en_plus > 1 ? "s" : ""} en plus`}
            </>
          ) : "Coût à confirmer"}
        </p>
        {(() => {
          const choisi = produits.find((p) => p.id === produitId);
          return choisi && devis !== null && devis.signataires_compris !== null ? (
            <p className="muted panneau__aide">
              {NOMS_OFFRE[choisi.code] ?? choisi.libelle} : {montant(devis.prix_offre_minor)} par enveloppe,{" "}
              {devis.signataires_compris} signataires compris, puis {montant(devis.supplement_signataire_minor ?? 0)} par signataire.
            </p>
          ) : null;
        })()}
        <p className="muted panneau__aide">
          {traitement
            ? "Débité de votre crédit à la création de l'enveloppe ; ramené au tarif « enveloppe non signée » si elle s'arrête avant la signature."
            : "Débité de votre crédit quand vous cliquez sur « Envoyer ce document »."}
        </p>
        <Erreur erreur={erreur} />
        {/* Franck, 01/11/2026 : ce qui empeche l'envoi, en rouge. */}
        {raison && <p className="alert alert--error" role="status">{raison}</p>}
        <div className="pied pied--panneau">
          <button type="button" className="btn btn--secondary" onClick={onAnnuler}>Annuler</button>
          <button type="button" className="btn btn--primary" disabled={!pret || envoi}
                  onClick={() => void creer()}>
            {envoi ? "Envoi…" : traitement ? "Créer et lancer le traitement" : "Envoyer ce document"}
          </button>
        </div>
      </section>
    </>
  );

  return apercu ? (
    <EditeurCadres
      data={apercu}
      signataires={noms}
      actif={actif}
      onActif={setActif}
      cadres={cadres}
      onChange={setCadres}
      onTelecharger={telechargerApercu}
      avant={avant}
      apres={apres}
      modelesOuverts={modelesOuverts}
      entete={
        <>
          {nomDepot} · {signataires.length} signataire{signataires.length > 1 ? "s" : ""} ·{" "}
          <button type="button" className="btn btn--link btn--inline" onClick={() => setEtape(0)}>
            ← document et signataires
          </button>
        </>
      }
    />
  ) : (
    <div className="prepa">
      <div className="prepa__document">
        <p className="muted">
          {apercuKo
            ? "L'aperçu n'a pas pu être affiché : les cadres de signature ne peuvent pas être posés, l'envoi est donc impossible. Rechargez la page ou déposez le document à nouveau."
            : "Chargement du document…"}
        </p>
      </div>
      <aside className="prepa__panneau">{avant}{apres}</aside>
    </div>
  );
}
